import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import "../auth/auth.css";

export default function Login() {
  const { login, loginWithToken, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const fromPath = useMemo(() => {
    const state = location.state;
    let stored = null;
    try {
      stored = sessionStorage.getItem("postLoginRedirect");
    } catch {
      stored = null;
    }
    return state?.from || stored || "/whiteboard";
  }, [location.state]);

  const [loginValue, setLoginValue] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  /* ===============================
     STUDENT AUTO LOGIN (FIXED)
  ================================*/
  // const studentAutoLogin = async (token) => {
  //   try {
  //     setSubmitting(true);
  //     await loginWithToken(token);
  //   } catch (err) {
  //     console.error("Student auto login failed", err);
  //     setError("Invalid or expired student link");
  //     setSubmitting(false);
  //   }
  // };

  const guestAutoLogin = async (token) => {
  try {
    setSubmitting(true);
    await loginWithToken(token);
  } catch (err) {
    console.error("Guest auto login failed", err);
    setError("Guest session expired or invalid");
    setSubmitting(false);
  }
};


  // useEffect(() => {
  //   const params = new URLSearchParams(location.search);
  //   const isStudent = params.get("student");
  //   const token = params.get("token");

  //   if (isStudent === "true" && token) {
  //     studentAutoLogin(token);
  //   }
  // }, [location.search]);

  useEffect(() => {
  // Example hash:
  // #room=abc123&guest=JWT_TOKEN
  const hash = location.hash.startsWith("#")
    ? location.hash.substring(1)
    : location.hash;

  const params = new URLSearchParams(hash);
  const guestToken = params.get("guest");

  if (guestToken) {
    guestAutoLogin(guestToken);
  }
}, [location.hash]);

  /* =============================== */

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(""), 5000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(""), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      try {
        sessionStorage.removeItem("postLoginRedirect");
      } catch {}
      navigate(fromPath, { replace: true });
    }
  }, [fromPath, isAuthenticated, navigate]);

  const validate = () => {
    const errors = {
      login: !loginValue.trim(),
      password: !password,
    };
    setValidationErrors(errors);
    return !errors.login && !errors.password;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setValidationErrors({});

    if (!validate()) return;

    setSubmitting(true);
    try {
      await login(loginValue, password);
    } catch (err) {
      const message =
        typeof err?.serverMessage === "string"
          ? err.serverMessage
          : typeof err?.message === "string"
          ? err.message
          : "";
      setError(message);
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo" role="img" aria-label="Black's Academy">
            <img
              src="https://www.blacksacademy.xyz/eik/SymbolSoftCream120.GIF"
              alt="Black's Academy Logo"
              height="100"
            />
          </div>
          <h1 className="auth-title">Welcome back</h1>
          <p className="auth-subtitle">
            Sign in to your Blacks Academy Whiteboard
          </p>
        </div>

        <form onSubmit={onSubmit} className="auth-form" noValidate>
          {successMessage && (
            <div className="auth-success" role="alert">
              <span>✅</span>
              {successMessage}
            </div>
          )}

          {error && (
            <div className="auth-error" role="alert">
              <span>⚠️</span>
              {error}
            </div>
          )}

          <label className="auth-label">
            <span>
              Username or Email
              <span className="required-star">*</span>
            </span>
            <input
              className={`auth-input ${
                validationErrors.login ? "input-error" : ""
              }`}
              value={loginValue}
              onChange={(e) => setLoginValue(e.target.value)}
              autoComplete="username"
              placeholder="e.g. student123"
              autoFocus
            />
          </label>

          <label className="auth-label">
            <span>
              Password
              <span className="required-star">*</span>
            </span>
            <div className="auth-input-wrapper">
              <input
                className={`auth-input ${
                  validationErrors.password ? "input-error" : ""
                }`}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                placeholder="••••••••"
              />
              <button
                type="button"
                className="auth-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? "🙈" : "👁️"}
              </button>
            </div>
          </label>

          <button className="auth-button" type="submit" disabled={submitting}>
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
